import React, { MouseEvent, ReactElement, useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { localize } from "src/l10n";
import { Breadcrumbs, PageHeader } from "src/ui";
import { UnitGroupNode, UnitGroupsViewState } from "./UnitGroupsView.types";
import { Conditional } from "src/components/Conditional";
import Api, { getCancelTokenSource } from "src/api";
import SpintrLoader from "src/ui/components/Loader";
import { flatten, Toggle } from "@fluentui/react";
import { FormFooterBar } from "src/ui/components/Forms";
import { CaptionBolder, SmallBody, SmallBodyBolder, UnitGroupChange, UnitGroupsDrawer } from "src/components";
import { SpintrTypes } from "src/typings";
import classNames from "classnames";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";

type ToggleChangeHandler = (
    unit: UnitGroupNode,
    event: MouseEvent<HTMLElement>,
    checked?: boolean,
) => void;

function mapUnitGroupNode(unit: UnitGroupNode): UnitGroupNode {
    return {
        ...unit,
        toggled: unit.groupId && unit.groupId > 0,
        expanded: unit.nodes && unit.nodes.length > 0,
        nodes: unit.nodes?.map(mapUnitGroupNode) || []
    };
}

function toggleUnitGroup(
    units: UnitGroupNode[] | undefined,
    target: UnitGroupNode,
    toggled: boolean
): UnitGroupNode[] {
    if (!units || units.length === 0) {
        return [];
    }

    return units.map((unit) => {
        if (unit.id === target.id) {
            return { ...unit, toggled };
        }

        if (!unit.nodes) {
            return unit;
        }
        
        return {
            ...unit,
            nodes: toggleUnitGroup(unit.nodes, target, toggled),
        };
    });
}

function getTotalItemCount(unit: UnitGroupNode): number {
    return 1 + (unit.nodes?.reduce((acc, unit) => acc + getTotalItemCount(unit), 0) || 0);
}

function getCumulativeIndex(units: UnitGroupNode[], index: number): number {
    if (index === 0) {
        return 0;
    }

    const slice = units.slice(0, index);
    
    return slice.reduce((acc, unit) => acc + getTotalItemCount(unit), 0);
}

function flattenNodes(units: UnitGroupNode[]): UnitGroupNode[] {
    return units.reduce((acc, unit) => {
        if (unit.nodes) {
            return [...acc, unit, ...flattenNodes(unit.nodes)];
        }

        return [...acc, unit];
    }, [] as UnitGroupNode[]);
}

function renderUnitGroupRow(unit: UnitGroupNode, onChange: ToggleChangeHandler, cumulativeIndex: number): ReactElement {
    const rowClassName = classNames("UnitGroupRow", {
        "even": cumulativeIndex % 2 === 0,
        "odd": cumulativeIndex % 2 !== 0
    });
    
    return (
        <div className="UnitGroupNode" key={unit.id}>

            <div className={rowClassName}>

                <div className="UnitGroupRow-name">
                    <SmallBody>
                        {unit.name}
                    </SmallBody>
                </div>

                {unit.type !== SpintrTypes.UberType.Office && unit.type !== SpintrTypes.UberType.Company && (
                    <div className="UnitGroupRow-toggle">
                        <Toggle
                            disabled={!!unit.groupId}
                            checked={unit.toggled}
                            onChange={(ev, checked) => onChange(unit, ev, checked)} />
                    </div>
                )}
            </div>

            <Conditional condition={unit.expanded && unit.nodes?.length > 0}>

                <div className="UnitGroupRow-children">
                    {unit.nodes?.map(
                        (unit, index) => renderUnitGroupRow(unit, onChange, cumulativeIndex + index + 1)
                    )}
                </div>

            </Conditional>
        </div>
    );
}

function UnitGroupsView(): ReactElement {
    const [state, setState] = useState<UnitGroupsViewState>({
        createDrawerOpen: false,
        nodes: [],
        loading: true
    });

    const history = useHistory();
    const enableGroupTemplate = useSelector<Spintr.AppState, boolean>(
        (appState) => appState.instance.get("enableGroupTemplates") as boolean,
    );

    const [changes, setChanges] = useState<UnitGroupChange[]>([]);

    const onToggleGroup = useCallback<ToggleChangeHandler>((unit, _, checked) => {
        setState((prevState) => ({
            ...prevState,
            nodes: toggleUnitGroup(prevState.nodes, unit, !!checked)
        }));

        setChanges((prevChanges) => {
            const index = prevChanges.findIndex((change) => change.id === unit.id);

            if (index === -1) {
                console.log("Adding change", unit.id, unit.name, unit.groupId, prevChanges);
                return [...prevChanges, {
                    id: unit.id,
                    changeType: checked ? "add" : "remove",
                    name: unit.name,
                    groupId: unit.groupId || 0,
                } as UnitGroupChange];
            }

            return prevChanges.filter((_, i) => i !== index);
        });
    }, [setState, setChanges]);

    const onToggleAll = useCallback((_: unknown, checked?: boolean) => {
        const setNodes  = (nodes: UnitGroupNode[]) => {
            return nodes.map((unit) => ({
                ...unit,
                toggled: !!checked || !!unit.groupId,
                nodes: unit.nodes ? setNodes(unit.nodes) : [],
            }));
        };

        setState((prevState) => ({
            ...prevState,
            nodes: setNodes(prevState.nodes),
        }));

        setChanges((_) => {
            if (!checked) {
                return [];
            }

            return flattenNodes(state.nodes)
                .filter((unit) => !unit.groupId && unit.type !== SpintrTypes.UberType.Office && unit.type !== SpintrTypes.UberType.Company)
                .map<UnitGroupChange>((unit) => ({
                    id: unit.id,
                    changeType: "add",
                    name: unit.name,
                    groupId: unit.groupId || 0,
                }));
        });
    }, [setState, setChanges, state.nodes]);

    const breadcrumbItems = useMemo<Spintr.IBreadcrumbItem[]>(
        () => [{
            text: localize("appOrganisations"),
            key: "organisation",
            link: "/admin/organisation"
        }, {
            text: localize("Grupper"),
            key: "unitgroups",
            link: "/admin/organisation/groups"
        }],
        [],
    );

    const onCloseDrawer = useCallback(() => {
        setState((prevState) => ({ ...prevState, createDrawerOpen: false }));
    }, []);

    useEffect(() => {
        const cts = getCancelTokenSource();

        Api.get<UnitGroupNode[]>("/api/v1/units/groups", { cancelToken: cts.token })
            .then((response => setState({
                createDrawerOpen: false,
                nodes: response.data.map(mapUnitGroupNode),
                loading: false
            })))
            .catch((_) => {});

        return () => cts.cancel();
    }, [setState]);

    const allChecked = flattenNodes(state.nodes)
        .filter((unit) => !unit.groupId && unit.type !== SpintrTypes.UberType.Office && unit.type !== SpintrTypes.UberType.Company)
        .every((unit) => unit.toggled);

    if (!enableGroupTemplate) {
        history.push("/admin/organisation");
        return null;
    }

    return (
        <div id="UnitGroupsView">
            <Helmet>
                <title>{localize("Organisation")}</title>
            </Helmet>

            <Breadcrumbs
                displayInstance={true}
                items={breadcrumbItems} />

            <PageHeader title={localize("Organisation")} />

            <Conditional condition={state.loading}>
                <SpintrLoader />
            </Conditional>

            <Conditional condition={!state.loading}>
                <div className="UnitGroupsView-list">
                    <div className="UnitGroupsView-listActions">
                        <div className="UnitGroupsView-listActions-toggle">
                            <Toggle
                                checked={allChecked}
                                inlineLabel={true}
                                label={localize("VaxlaAllt")}
                                onChange={onToggleAll} />
                        </div>
                    </div>
                    <div className="UnitGroupsView-listHeader">
                        <SmallBodyBolder className="UnitGroupsView-listHeader-name" color="contentDark">
                            {localize("Organisation")}
                        </SmallBodyBolder>
                        <SmallBodyBolder className="UnitGroupsView-listHeader-toggle" color="contentDark">
                            {localize("Aktiv")}
                        </SmallBodyBolder>
                    </div>
                    <div className="UnitGroupsView-listBody">
                        {state.nodes.map(
                            (unit, index) => renderUnitGroupRow(
                                unit,
                                onToggleGroup,
                                getCumulativeIndex(state.nodes, index)
                            ),
                        )}
                    </div>
                </div>
            </Conditional>

            <FormFooterBar
                disableSaveButton={changes.length === 0}
                hideCancelButton={true}
                onSaveClick={() => setState((prevState) => ({ ...prevState, createDrawerOpen: true }))}
                saveText={changes.length === 0
                    ? localize("CONTINUE")
                    : `${localize("CONTINUE")} (${changes.length})`
                } />

            <UnitGroupsDrawer
                changes={changes}
                onDismiss={onCloseDrawer}
                open={state.createDrawerOpen} />
        </div>
    );
}

export default UnitGroupsView;
